import { useEffect, useRef, useState, useCallback } from "react";
import { logout } from "../utility/auth";
import toast from "react-hot-toast";

/**
 * Custom hook to handle user inactivity
 * @param {number} warningTime - Time in milliseconds before showing warning (default: 12 minutes)
 * @param {number} logoutTime - Time in milliseconds before auto logout (default: 15 minutes)
 */
const useInactivityTimer = (
  warningTime = 12 * 60 * 1000,
  logoutTime = 15 * 60 * 1000,
) => {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    Math.floor(logoutTime / 1000),
  );

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const showWarningRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Format remaining time as MM:SS
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    showWarningRef.current = false;

    // Clear all timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownIntervalRef.current)
      clearInterval(countdownIntervalRef.current);

    toast.error("You have been logged out due to inactivity", {
      duration: 3000,
    });

    // Call logout function
    setTimeout(() => {
      logout();
    }, 500);
  }, []);

  // Reset all timers
  const resetTimers = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    showWarningRef.current = false;

    // Clear existing timers
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Set initial remaining time to full logout time
    setRemainingTime(Math.floor(logoutTime / 1000));

    // Start countdown interval that tracks time since last activity
    countdownIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, Math.floor((logoutTime - elapsed) / 1000));

      // Only update state (and cause re-renders) when the warning modal is visible
      if (showWarningRef.current) {
        setRemainingTime(remaining);
      }

      // Check if we should show warning
      if (elapsed >= warningTime && !showWarningRef.current) {
        setShowWarning(true);
        showWarningRef.current = true;
        setRemainingTime(remaining);
        toast.error(
          "You will be logged out due to inactivity soon. Move your mouse or press any key to stay logged in.",
          {
            duration: 5000,
            icon: "⚠️",
          },
        );
      }

      // Check if we should logout
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        handleLogout();
      }
    }, 1000);
  }, [warningTime, logoutTime, handleLogout]);

  // Handle user activity - use ref to avoid stale closure
  const handleActivity = useCallback(() => {
    const timeSinceLastActivity = Date.now() - lastActivityRef.current;

    // Reset if warning is shown or if enough time has passed since last activity
    if (showWarningRef.current || timeSinceLastActivity > 30000) {
      resetTimers();
    } else {
      // Just update last activity time without full reset
      lastActivityRef.current = Date.now();
    }
  }, [resetTimers]);

  useEffect(() => {
    // Events to track user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, true);
    });

    // Initialize timers
    resetTimers();

    // Capture refs for cleanup
    const warningTimer = warningTimerRef.current;
    const logoutTimer = logoutTimerRef.current;
    const countdownInterval = countdownIntervalRef.current;

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity, true);
      });

      // Clear all timers
      if (warningTimer) clearTimeout(warningTimer);
      if (logoutTimer) clearTimeout(logoutTimer);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [handleActivity, resetTimers]);

  return {
    showWarning,
    remainingTime,
    formatTime,
    resetTimers,
  };
};

export default useInactivityTimer;
